/**
 * Sailing guide content sourced from `docs/sailing_concepts.md`.
 * Each entry maps to a wind-relative category produced by `windRelative()`
 * in `src/components/RouteMap.js`.
 */

export const SAILING_GUIDE = {
  head: {
    name: 'No-Go Zone',
    emoji: '🚫',
    desc: 'You are pointing within ~22.5° of the true wind direction — too close to sail effectively. The sails will luff (flutter), lose power, and the boat will slow to a stop. This zone is about 45° wide in total (22.5° on each side of the wind).',
    setup: 'Sails not driving — they will be luffing hard.',
    checklist: [
      'Feel the wind dead ahead or very near to the bow.',
      'Watch the sails flutter and lose all power.',
      'Bear away (turn the bow away from the wind) until the sails fill and stop fluttering.',
    ],
  },
  'close-hauled': {
    name: 'Close-Hauled (upwind)',
    emoji: '⛵',
    desc: 'Sailing as close to the wind as practical — TWA ~22° to 60°. This is the range from just outside the no-go zone through the tightest efficient upwind angle for a typical cruising boat. Optimal upwind performance is found nearer to 45°–55°.',
    setup: 'Sheet the sails all the way in. Sails trimmed flat and pulled tight.',
    checklist: [
      'Feel the wind on your face when looking forward.',
      'Sheet the sails all the way in — they should be close to the boat\'s centreline.',
      "Steer to the jib using the telltales and the flutter of its leading edge; aim for smooth airflow.",
      'Helmsman sits on the high (windward) side, opposite the boom.',
      'Crew trims sails and shifts weight to keep the boat from heeling too much or too little.',
      'If the sails begin to luff, bear away a few degrees until they refill; never push into the No-Go Zone.',
    ],
  },
  'close-reach': {
    name: 'Close Reach',
    emoji: '🧭',
    desc: 'Wind forward of the beam — TWA 60°–80°. A close reach is a lively, powered-up point of sail between close-hauled and a beam reach. The boat is still footing off enough to generate good speed while staying well clear of the no-go zone.',
    setup: 'Ease sails progressively compared with close-hauled. Start with sheets about a quarter of the way out and adjust for smooth airflow.',
    checklist: [
      'Feel the wind in your face, but at a noticeable angle — no longer dead ahead.',
      'Ease the sails part-way out compared with close-hauled.',
      'Steer toward an object or landmark.',
      'Adjust the sails to changes in boat direction or wind shifts.',
      'Watch for the jib to guide trimming; keep it full and drawing.',
    ],
  },
  beam: {
    name: 'Beam Reach (across the wind)',
    emoji: '💨',
    desc: 'Sailing perpendicular to the wind with the wind coming over the side of the boat — TWA 80°–100°. A fast and easy point of sail — a great place to start learning to steer and trim.',
    setup: 'Sheet the sails about halfway in.',
    checklist: [
      'Feel the wind coming across the boat.',
      'Sheet the sails about halfway in.',
      'Steer towards an object or landmark.',
      "Adjust the sails to changes in the boat's direction or changes in wind direction.",
      "Expect a slight heel — it's normal and lively at this point of sail.",
    ],
  },
  broad: {
    name: 'Broad Reach (downwind at an angle)',
    emoji: '🌊',
    desc: 'Wind over the aft quarter — TWA 100°–150°. Comfortable and smooth, and generally faster, safer and easier than dead downwind.',
    setup: 'Sheet the sails so they are almost perpendicular to the wind, eased further for angles over 120°.',
    checklist: [
      'Feel the wind on the back of your neck when facing forward.',
      'Sheet in the sails so they\'re almost perpendicular to the wind.',
      'Steer towards an object or landmark.',
      'Watch the jib as an early warning for an accidental gybe — if it goes limp and starts to cross the boat, push the tiller toward the boom until the jib refills.',
    ],
  },
  run: {
    name: 'Running / Dead Downwind',
    emoji: '🏖️',
    desc: 'Sailing directly away from the wind with the wind coming from behind — TWA 150°–180°. The most comfortable point of sail, but watch out: with the wind directly astern there is more chance of an accidental gybe where the boom flies across the boat.',
    setup: 'Sails eased right out to catch the following wind.',
    checklist: [
      'Feel the wind on the back of your neck.',
      'Let the sails right out, roughly perpendicular to the wind.',
      'Steer towards an object or landmark.',
      'Watch for an accidental gybe — the boom can suddenly swing across the boat.',
      'If the jib goes limp and begins to cross the boat, push the tiller toward the boom until it fills again.',
      'Prefer a broad reach when possible — it is faster, safer and easier to control.',
    ],
  },
};

/** Map a windRel label to a guide key. */
export function guideKeyForRel(windRel) {
  if (!windRel) return null;
  if (windRel === 'Head') return 'head';
  if (windRel === 'Run') return 'run';
  const lower = windRel.toLowerCase();
  if (lower.includes('close-hauled')) return 'close-hauled';
  if (lower.includes('close reach')) return 'close-reach';
  if (lower.includes('beam reach')) return 'beam';
  if (lower.includes('broad reach')) return 'broad';
  return null;
}

export function guideForRel(windRel) {
  const key = guideKeyForRel(windRel);
  return key ? SAILING_GUIDE[key] : null;
}